import fastdom from 'fastdom';
import g from '#core/global';
import events from '#core/events';
import util from '#core/util';
import pattern_config from '#core/pattern-config';
import load from '#core/load';
import layout from '#core/layout';


const state = window.teal_state = window.teal_state || {};
state.reveal = state.reveal || {};

/**
 * Breakpoint ID
 * Resolves breakpoint names from pattern-config to pixel values via layout module
 */
const breakpoint_names = [
	pattern_config.reveal.bp_small,
	pattern_config.reveal.bp_medium,
	pattern_config.reveal.bp_large,
];
window.breakpoint_id = 0;

/**
 * Calculate the current breakpoint ID based on viewport width
 */
function calculate_breakpoint_id() {
	const bp = state.layout && state.layout.breakpoints;
	if (!bp || !bp[breakpoint_names[0]]) return;

	const width = window.innerWidth;
	window.breakpoint_id = breakpoint_names.findIndex(name => {
		return width < bp[name];
	});
	if (window.breakpoint_id === -1) {
		window.breakpoint_id = breakpoint_names.length;
	}
}

document.addEventListener(events.layout.breakpoints_ready, calculate_breakpoint_id);
window.addEventListener('resize/debounced', calculate_breakpoint_id);


const namespace = 'reveal';
const reveal_attr = g.reveal_attr;
const target_attr = 'reveal-target';
const group_attr = 'reveal-group';
const busy_attr = g.revealing_attr;
const done_attr = g.revealed_attr;
const sequence_prop = '--reveal-sequence';

const subreveal_attr = 'subreveal';
const aligned_attr = 'aligned';
const delay_attr = 'delay';
const image_attr = 'reveal-image';

const target_type_children = 'children';
const target_type_target = 'target';


const selector = `[${reveal_attr}]:not([${reveal_attr}*="+"], [${subreveal_attr}])`;
const margins = '1% 1% -10% 1%';

// Delay values greater than this are treated as fixed delays in milliseconds
const css_delay_count = 24;

let has_reveal = false;


const module = {
	/**
	 * Init module
	 */
	init() {
		if (load.ran('reveal')) return false;

		load.now(() => {
			has_reveal = assign_elements();
			if (!has_reveal) return false;

			move_attributes_to_targets();
			setup_reveals();
			setup_aligned();
			setup_reveal_group();
		});
		load.content(() => {
			has_reveal = assign_elements();
			if (!has_reveal) return false;
		});
		load.all(() => {
			has_reveal = assign_elements();
			if (!has_reveal) return false;
		});
	},
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_reveal) return true;
	return true;
}


/**
 * Move attributes to targets
 */
function move_attributes_to_targets() {
	const init = function() {
		const $reveal_parents = document.querySelectorAll(`[${target_attr}]`);
		if (!$reveal_parents.length) return;

		$reveal_parents.forEach($parent => {
			let $targets = [];
			const target_type = $parent.getAttribute(target_attr);
			if (target_type === target_type_children) {
				$targets = $parent.children;
			} else if (target_type === target_type_target) {
				$targets = $parent.querySelectorAll(`[${target_attr}]`);
			} else if (util.isValidSelector(target_type)) {
				$targets = $parent.querySelectorAll(target_type);
			}

			const attributes = [
				reveal_attr,
				delay_attr,
				group_attr,
				aligned_attr,
				image_attr,
			];

			// Check if parent has been processed before
			const has_templates = $parent.hasAttribute('data-reveal-template');

			attributes.forEach(attr => {
				let value;

				// Get value from template or original attribute
				if (has_templates) {
					const template_attr = (attr === reveal_attr)
						? 'data-reveal-template'
						: `data-reveal-template-${attr}`;
					value = $parent.getAttribute(template_attr);
				} else {
					value = $parent.getAttribute(attr);
					if (value !== null) {
						const template_attr = (attr === reveal_attr)
							? 'data-reveal-template'
							: `data-reveal-template-${attr}`;
						$parent.setAttribute(template_attr, value);
					}
				}

				if (value === null) return false;

				// Apply to targets that have not been revealed
				[...$targets].forEach($target => {
					if (attr === reveal_attr) {
						if (
							!$target.hasAttribute(reveal_attr)
							&& !$target.hasAttribute(done_attr)
							&& !$target.hasAttribute(busy_attr)
						) {
							$target.setAttribute(attr, value);
						}
					} else {
						if (!$target.hasAttribute(attr)) {
							$target.setAttribute(attr, value);
						}
					}
				});
			});

			// Prevent parent from animating itself
			$parent.removeAttribute(reveal_attr);
		});
	};

	window.addEventListener(events.global.reinitialize, init);
	init();
}


/**
 * Check if element is in viewport, even if clipped by overflow: hidden
 * @param {HTMLElement} $element
 * @return {boolean}
 */
function is_in_viewport($element) {
	if (!$element.offsetParent) return false;

	const rect = $element.getBoundingClientRect();
	const viewport_height = window.innerHeight || document.documentElement.clientHeight;
	const viewport_width = window.innerWidth || document.documentElement.clientWidth;

	return (
		rect.left < viewport_width
		&& rect.right > 0
		&& rect.top < viewport_height
		&& rect.bottom > 0
	);
}


/**
 * Check if element is clipped by overflow:hidden parent
 * @param {HTMLElement} $element
 * @return {Object}
 */
function is_clipped_by_overflow($element) {
	const rect = $element.getBoundingClientRect();
	let parent = $element.parentElement;

	while (parent && parent !== document.body) {
		const parent_style = window.getComputedStyle(parent);
		if (
			parent_style.overflow === 'hidden'
			|| parent_style.overflowY === 'hidden'
			|| parent_style.overflowX === 'hidden'
		) {
			const parent_rect = parent.getBoundingClientRect();

			// Check if element is outside parent bounds
			if (
				rect.left > parent_rect.right
				|| rect.right < parent_rect.left
				|| rect.top > parent_rect.bottom
				|| rect.bottom < parent_rect.top
			) {
				return {
					is_clipped: true,
					clipping_parent: parent,
				};
			}
		}
		parent = parent.parentElement;
	}

	return {
		is_clipped: false,
		clipping_parent: null,
	};
}


/**
 * Wait for all images within an element to load
 * @param {HTMLElement} $element
 * @return {Promise}
 */
function wait_for_images($element) {
	const $images = $element.querySelectorAll('img');
	if (!$images.length) return Promise.resolve();

	const image_promises = Array.from($images).map($img => {
		if ($img.complete && $img.naturalHeight !== 0) {
			return Promise.resolve();
		}

		// Wait for load or error
		return new Promise((resolve) => {
			$img.addEventListener('load', resolve, { once: true });
			$img.addEventListener('error', resolve, { once: true });
		});
	});

	return Promise.all(image_promises);
}


/**
 * Reveals v20250315.1730* (* insta reveal mod)
 * Trigger animations on scroll
 * @requires wilsonpage/fastdom
 * @requires v.breakpoints
 */
function setup_reveals() {
	const revealed_groups = [];

	const get_delay = function($element) {
		if ($element.hasAttribute(delay_attr)) {
			const responsive_delay = $element.getAttribute(delay_attr);
			const matches = responsive_delay.match(/^([^\/]+)?\/?([^\/]+(?=\/\/))?(?:\/{2})?((?<=\/\/)[^\/]+)?\/?([^\/]+)?$/);
			if (matches) {
				for (let i = (window.breakpoint_id || 0); i >= 0; i--) {
					if (matches[i + 1]) {
						return matches[i + 1];
					}
				}
			}
			return false;
		}

		const reveal_type = $element.getAttribute(reveal_attr) || false;
		return (reveal_type)
			? (reveal_type + '').split('/')[1]
			: false;
	};

	const process = function($element, is_subreveal = false) {
		const handle_reveal = () => {
			const delay = get_delay($element);
			if (delay && delay > css_delay_count) {
				setTimeout(() => {
					reveal({$element, is_subreveal});
				}, delay);
			} else {
				reveal({$element, is_subreveal});
			}
		};

		// Check if we need to wait for images
		if ($element.hasAttribute(image_attr)) {
			wait_for_images($element).then(handle_reveal);
		} else {
			handle_reveal();
		}
	};

	const reveal = function({$element, is_subreveal = false, is_in_group = false} = {}) {
		if ($element.hasAttribute(busy_attr)) return false;
		if ($element.hasAttribute(done_attr)) return false;

		// If element is in a group, reveal all elements in the group
		if (!is_in_group) {
			const group_id = $element.getAttribute(group_attr);
			if (group_id && !revealed_groups.includes(group_id)) {
				revealed_groups.push(group_id);
				const $group_elements = document.querySelectorAll(`[${group_attr}="${group_id}"]`);
				$group_elements.forEach($group_element => {
					if ($group_element !== $element) {
						reveal({$element: $group_element, is_in_group: true});
					}
				});
			}

			// If element is in a group that is already being revealed, skip it
			else if (group_id && revealed_groups.includes(group_id)) {
				return false;
			}
		}

		fastdom.mutate(() => {
			if (!is_subreveal) {
				$element.querySelectorAll(`[${subreveal_attr}]`).forEach($subreveal => {
					if ($subreveal.closest(`[${reveal_attr}]:not([${subreveal_attr}])`) === $element) {
						process($subreveal, true);
					}
				});
			}

			const reveal_type = $element.getAttribute(reveal_attr);
			if (reveal_type === null) return false;

			if (
				reveal_type === ''
				|| reveal_type.startsWith('/')
				|| reveal_type.startsWith('*')
			) {
				$element.removeAttribute(reveal_attr);
				$element.setAttribute(done_attr, reveal_type);
				$element.dispatchEvent(new CustomEvent(events.reveal.end));
			} else {
				$element.setAttribute(busy_attr, reveal_type);
				$element.dispatchEvent(new CustomEvent(events.reveal.start));
				let is_transition_done = false;

				const revealed_callback = function() {
					if (is_transition_done) return false;

					is_transition_done = true;
					$element.removeAttribute(reveal_attr);
					$element.removeAttribute(busy_attr);
					$element.setAttribute(done_attr, reveal_type);
					$element.dispatchEvent(new CustomEvent(events.reveal.end));
					$element.removeEventListener(g.transition_end_e, try_reveal);
				};

				const try_reveal = (event) => {
					if (event.target === $element) {
						revealed_callback();
					}
				};

				$element.addEventListener(g.transition_end_e, try_reveal);

				// If [reveal] is applied to an element that does not have a transition,
				// the "transitionend" event will not fire, so we need to manually mark
				// it as having been revealed
				const style = window.getComputedStyle($element);
				if (!style.transitionProperty || style.transitionProperty === 'none') {
					revealed_callback();
				} else {
					// Safety net: if transitionend never fires even on a transitioned element,
					// finalize after the element's longest transition + a small buffer
					// (is_transition_done guard prevents double-firing)
					const parse_s = (v) => parseFloat(v) || 0;
					const durations = style.transitionDuration.split(',').map(parse_s);
					const delays = style.transitionDelay.split(',').map(parse_s);
					const max_ms = Math.max(...durations.map((d, i) => d + (delays[i] || 0))) * 1000;
					setTimeout(revealed_callback, max_ms + 100);
				}
			}
		});
	};

	const init_reveals = function() {
		const observer = new IntersectionObserver(function(entries, observer){
			entries.forEach(function(entry) {
				if (entry.isIntersecting) {
					process(entry.target);
					observer.unobserve(entry.target);
				}
			});
		}, { rootMargin: margins });

		// Immediately reveal elements already in view on page load, or observe
		setTimeout(function() {
			layout.eachOnce(selector, namespace, function(index, $element) {
				const clipping_data = is_clipped_by_overflow($element);
				const rect = $element.getBoundingClientRect();

				if (
					$element.offsetParent
					&& (
						rect.bottom < 100
						|| (clipping_data.is_clipped && is_in_viewport($element))
					)
				) {
					reveal({$element});
				} else {
					observer.observe($element);
				}
			});
		}, 100);

		// TODO: test this
		layout.eachOnce(`[${reveal_attr}^="*"]`, namespace, function(index, $element) {
			process($element);
		});
	};

	window.addEventListener(events.global.reinitialize, init_reveals);
	init_reveals();
}


/**
 * (v) Reveal Delays Sequencer v20240328.1945
 * Sequence elements in the same row and in groups
 */
function setup_aligned() {
	const init_aligned = function() {
		const $elements = document.querySelectorAll(`[${aligned_attr}]`);
		const data = {};

		// Group elements by their "aligned" attribute and vertical position
		$elements.forEach($element => {
			if (!$element.offsetParent) return false;

			const bounds = $element.getBoundingClientRect();
			const group_id = $element.getAttribute(aligned_attr) || 0;
			const row_id = `row_${Math.ceil(bounds.top)}`;

			data[group_id] ??= {};
			data[group_id][row_id] ??= [];
			data[group_id][row_id].push({
				$element: $element,
				left: Math.ceil(bounds.left),
			});
		});

		// Ignore rows with only one element
		for (const group_id in data) {
			for (const row_id in data[group_id]) {
				const row_set = data[group_id][row_id];
				if (row_set.length > 1) continue;

				row_set.forEach(row_item => {
					row_item.$element.removeAttribute(aligned_attr);
				});
				delete data[group_id][row_id];
			}
		}

		// Set sequence order based on horizontal position
		for (const group_id in data) {
			for (const row_id in data[group_id]) {
				const row_set = data[group_id][row_id];
				row_set.sort((a, b) => a.left - b.left);
				row_set.forEach((row_item, index) => {
					row_item.$element.style.setProperty(sequence_prop, index);
				});
			}
		}
	};

	// TODO: when are these fired?
	window.addEventListener(events.global.reinitialize, init_aligned);
	window.addEventListener(events.global.recalculate, init_aligned);
	init_aligned();
}


/**
 * Group elements to be revealed in a sequence
 * @todo consolidate this with "aligned"
 */
function setup_reveal_group() {
	const init_reveal_group = function() {
		const $elements = document.querySelectorAll(`[${group_attr}]`);
		const data = {};

		// Group elements by their "reveal-group" attribute
		$elements.forEach($element => {
			if (!$element.offsetParent) return false;

			const bounds = $element.getBoundingClientRect();
			const group_id = $element.getAttribute(group_attr) || 0;

			data[group_id] ??= [];
			data[group_id].push({
				$element: $element,
				left: Math.ceil(bounds.left),
				top: Math.ceil(bounds.top),
			});
		});

		// Ignore groups with only one element
		for (const group_id in data) {
			const group = data[group_id];
			if (group.length > 1) continue;

			group.forEach(group_item => {
				group_item.$element.removeAttribute(group_attr);
			});
			delete data[group_id];
		}

		// Set sequence order by position
		for (const group_id in data) {
			const group = data[group_id];
			group.sort((a, b) => {
				if (a.top !== b.top) {
					return a.top - b.top;
				}
				return a.left - b.left;
			});
			group.forEach((group_item, index) => {
				group_item.$element.style.setProperty(sequence_prop, index);
			});
		}
	};

	// TODO: when are these fired?
	window.addEventListener(events.global.reinitialize, init_reveal_group);
	window.addEventListener(events.global.recalculate, init_reveal_group);
	init_reveal_group();
}


// TODO: use clustering to group elements by proximity, not exact position

/**
 * Cluster values into groups based on proximity
 * @param {array} values
 * @param {int} threshold
 * @return {array}
 *
 * @example [21, 25, 60, 58, 64, 68, 69, 72]
 */
function cluster(values, threshold = 10) {
	const sorted = [...values].sort((a, b) => a - b);

	const clusters = [];
	let current_cluster = [sorted[0]];

	for (let i = 1; i < sorted.length; i++) {
		const previous_value = sorted[i - 1];
		const current_value = sorted[i];

		if (Math.abs(current_value - previous_value) <= threshold) {
			current_cluster.push(current_value);
		} else {
			clusters.push(current_cluster);
			current_cluster = [current_value];
		}
	}

	clusters.push(current_cluster);
	return clusters;
}
